/**
 * Open-redirect defense for the post-login `?redirect=` target.
 *
 * The client-v2 middleware bounces an unauthenticated user to the legacy login
 * with `?redirect=<originally-requested URL>`. After a successful login we send
 * the browser there — but ONLY after proving the target is one we trust, so an
 * attacker can't craft `/Login?redirect=https://evil.com` and phish a freshly
 * authenticated user.
 *
 * Accepts (returns the target):
 *  - a same-origin relative path (`/CustomerProfile/...`) — resolved against the
 *    page's own origin by the browser.
 *  - an absolute URL whose origin is in `allowedOrigins` (the login page's own
 *    origin plus the client-v2 app origin — CustomerProfile lives on client-v2,
 *    a DIFFERENT origin than this legacy login, so relative-only is not enough).
 *
 * Rejects (returns null → caller falls back to its default destination):
 *  - non-string / empty input
 *  - inputs containing CR/LF/tab (browsers strip these before navigation, so
 *    `/\t//evil.com` could otherwise resolve to `///evil.com`)
 *  - protocol-relative `//host` and the backslash trick `/\host`
 *  - any path that doesn't start with `/`
 *  - absolute URLs to a non-allowlisted origin, or non-http(s) schemes
 *    (`javascript:`, `data:`, etc.)
 */
const toOrigin = (value) => {
  try {
    return new URL(value).origin;
  } catch (e) {
    return null;
  }
};

export const sanitizeRedirect = (redirect, allowedOrigins = []) => {
  if (typeof redirect !== 'string' || redirect.length === 0) return null;
  if (/[\r\n\t]/.test(redirect)) return null;

  // Same-origin relative path. Reject protocol-relative (`//`) and the
  // backslash trick (`/\`) that some browsers treat as protocol-relative.
  if (redirect.startsWith('/')) {
    if (redirect.startsWith('//')) return null;
    if (redirect.startsWith('/\\')) return null;
    return redirect;
  }

  // Absolute URL: allow only http(s) to a trusted origin.
  let parsed;
  try {
    parsed = new URL(redirect);
  } catch (e) {
    return null;
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') return null;

  const allowed = allowedOrigins.map(toOrigin).filter(Boolean);
  if (!allowed.includes(parsed.origin)) return null;

  return redirect;
};

export default sanitizeRedirect;
